// src/pages/ConversationsPage.tsx

import { useNavigate } from 'react-router-dom';
import {
  useGetConversationsQuery,
  useCreateConversationMutation,
} from '../api/conversationsApi';
import { useActiveWorkspace } from '../hooks/useActiveWorkspace';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorState from '../components/common/ErrorState';
import Button from '../components/common/Button';

export default function ConversationsPage() {
  const navigate = useNavigate();
  // Replaced with useActiveWorkspace
  const activeWorkspace = useActiveWorkspace();

  const {
    data: conversations,
    isLoading,
    error,
    refetch,
  } = useGetConversationsQuery(activeWorkspace?.workspace.id, {
    skip: !activeWorkspace,
  });

  const [createConversation, { isLoading: isCreating }] = useCreateConversationMutation();

  const handleCreateConversation = async () => {
    if (!activeWorkspace) return;
    try {
      const result = await createConversation({
        workspaceId: activeWorkspace.workspace.id,
      }).unwrap();
      navigate(`/conversations/${result.session.id}`);
    } catch (error) {
      console.error('Failed to create conversation:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return <ErrorState message="Failed to load conversations." onRetry={refetch} />;
  }

  if (!activeWorkspace) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Please select a workspace to view conversations.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Conversations</h1>
        <Button onClick={handleCreateConversation} isLoading={isCreating}>
          New Conversation
        </Button>
      </div>

      {conversations?.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">No Conversations Yet</h3>
          <p className="text-gray-500 mb-4">Start a new conversation to get going.</p>
          <Button onClick={handleCreateConversation} isLoading={isCreating}>
            Start Conversation
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {conversations?.map((conversation) => (
            <button
              key={conversation.id}
              onClick={() => navigate(`/conversations/${conversation.id}`)}
              className="w-full text-left bg-white rounded-xl p-4 shadow-sm border border-gray-200 hover:border-gray-300 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="min-w-0 flex-1">
                  <h3 className="font-medium text-gray-900 truncate">
                    {conversation.title || 'Untitled Conversation'}
                  </h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                      conversation.status === 'active' ? 'bg-green-100 text-green-700' :
                      conversation.status === 'pending_human' ? 'bg-yellow-100 text-yellow-700' :
                      conversation.status === 'human_active' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {conversation.status.replace('_', ' ')}
                    </span>
                    <span className="text-xs text-gray-400 capitalize">
                      {conversation.source} · {conversation.mode}
                    </span>
                  </div>
                </div>
                <span className="text-xs text-gray-400 flex-shrink-0 ml-4">
                  {new Date(conversation.updatedAt).toLocaleDateString()}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
